import { get, isEmpty, isEqual, max, range, uniq, uniqBy, uniqWith, without } from 'lodash';

import { Action } from '../../actions';
import {
    ADD_BANNER_SITELINK,
    AddBannerSiteLink,
    CHECK_BANNER_SITELINK_HREF_STATUS,
    CheckBannerSitelinkHrefStatus,
    CLEAR_SITE_LINKS,
    ClearSiteLinks,
    CLOSE_SITE_LINKS_CANVAS,
    CREATE_SITE_LINKS_DRAFT_DATA,
    CreateSiteLinksDraftDataAction,
    OPEN_SITE_LINKS_CANVAS,
    OPEN_SITE_LINKS_EDITOR,
    OpenSiteLinksCanvas,
    OpenSiteLinksEditor,
    REMOVE_BANNER_SITELINK,
    RemoveBannerSiteLink,
    RESET_BANNER_SITELINK_FIELDS,
    ResetBannerSitelinkFieldsAction,
    SAVE_SITE_LINKS,
    SAVE_SITE_LINKS_SUCCESS,
    SaveSiteLinksSuccess,
    SHOW_SITE_LINKS_ERRORS,
    TOGGLE_SITE_LINKS_DESCRIPTION_FLAG,
    UPDATE_DRAFT_SITE_LINKS,
    UPDATE_PROTOCOL_DRAFT_SITE_LINKS,
    UPDATE_SITE_LINK_HREF_STATUS,
    UPDATE_SITELINKS_DATA,
    UpdateDraftSiteLinks,
    UpdateProtocolDraftSiteLinks,
    UpdateSiteLinkHrefStatus
} from '../../actions/banners-edit/banner-sitelinks';
import {
    ACCEPT_MASS_ACTIONS_SITE_LINKS_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_TRACKING_PARAMETERS_BULK_REPLACEMENT_EDITOR,
    ON_OPEN_MASS_ACTIONS_SITE_LINKS_EDITOR,
    SAVE_MASS_ACTIONS_LINK_BULK_PATH_REPLACEMENT_EDITOR,
    SAVE_MASS_ACTIONS_LINK_BULK_REPLACEMENT_EDITOR,
    SAVE_MASS_ACTIONS_TEXT_BULK_REPLACEMENT_EDITOR
} from '../../actions/banners-edit/banners-edit-mass-actions';
import {
    GET_TURBOLANDING_INFO_FOR_SITELINK,
    GetTurbolandingInfoForSitelink,
    UPDATE_TURBOLANDING_INFO_FOR_SITELINK,
    UpdateTurbolandingForSitelink
} from '../../actions/banners-edit/turbolanding';
import {
    ADD_NEW_TEXT_BANNERS,
    AddNewTextBannersAction,
    COPY_BANNER,
    COPY_PREV_BANNER,
    CopyBanner,
    CopyPrevBanner,
    CREATE_NEW_TEXT_BANNER,
    CreateNewTextBannerAction,
    ON_COPY_PREV_EDITED_BANNER,
    OnCopyPrevEditedBanners,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess,
    SET_DEFAULT_BANNER_SITELINKS,
    SetDefaultBannerSitelinks
} from '../../actions/bannersEdit';
import { SiteLinkField } from '../../components/SiteLink/SiteLink';
import {
    selectBannersDraftEditDataByTextReplace,
    selectBannersDraftEditSiteLinksDataByLinkReplace,
    selectBannersDraftEditSiteLinksDataByPartLinkReplace,
    selectBannersDraftEditSiteLinksDataByTrackingParametersReplace,
    selectSiteLinksEditDataBySiteLinksReplace
} from '../../selectors/bannerEdit/massActions/massActions';
import { sitelinksErrorsTextMapper } from '../../service/BannerEditErrors/SitelinksErrorsTextMapper/SitelinksErrorsTextMapper';
import { createSiteLinksByBannersApiData, getDefaultSiteLinkData } from '../../service/BannerHelpers/BannerHelpers';
import { buildErrorTree, mapTextToErrors } from '../../service/ErrorsMapper/ErrorsMapper';
import { LangUtils } from '../../service/Lang/Lang';
import { UrlHelpers } from '../../service/UrlHelpers';
import { BannerId, BannerSiteLinksId, SiteLinkData } from '../../typings/banners';
import {
    bannerHasSitelinks,
    BannerSiteLinksEditorData,
    BannersSiteLinksEditorState,
    EditBannersState,
    EditedBanner,
    MassActionId,
    ProtocolValue
} from '../../typings/bannersEditState';
import { PageStatus } from '../../typings/common';
import { State } from '../../typings/state';

const openSiteLinksEditor = (
    state: BannersSiteLinksEditorState,
    action: OpenSiteLinksEditor,
    fullState: State
): BannersSiteLinksEditorState => {
    if (action.bannerId === MassActionId.bannersEdit) {
        return {
            ...state,
            data: {
                ...state.data,
                [action.bannerId]: {
                    ...state.data[action.bannerId],
                    items: range(4).map(getDefaultSiteLinkData),
                    errors: {}
                }
            }
        };
    }

    const banner = fullState.editBanners.banners[action.bannerId];

    if (!bannerHasSitelinks(banner)) {
        return state;
    }

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...state.data[action.bannerId],
                items: banner.sitelinks.items,
                isDescriptionChecked: true,
                errors: {}
            }
        }
    };
};

const openSiteLinksCanvas = (
    state: BannersSiteLinksEditorState,
    action: OpenSiteLinksCanvas,
    fullState: State
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId].items[action.index];

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...state.data[action.bannerId],
                isCanvasOpened: true,
                currentSitelink: action.index,
                turboApiUrl: UrlHelpers.getTurboConstructorUrl({
                    host: fullState.editBanners.constants.canvas.turbolandingUnifiedApiUrl,
                    clientId: fullState.editBanners.clientInfo.clientId,
                    lang: LangUtils.getCurrentLang(),
                    selectedId: siteLinks.turbo ? +siteLinks.turbo.landingId : undefined
                })
            }
        }
    };
};

const updateSiteLinks = (
    state: BannersSiteLinksEditorState,
    action: UpdateDraftSiteLinks
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const items = [...siteLinks.items];
    const sitelinksErrors = siteLinks.errors;

    if (sitelinksErrors[action.index]) {
        delete sitelinksErrors[action.index][action.field];
        if (isEmpty(sitelinksErrors[action.index])) {
            delete sitelinksErrors[action.index];
        }
    }

    if (action.field === SiteLinkField.link) {
        const link = action.value;

        items[action.index] = {
            ...items[action.index],
            [SiteLinkField.link]: UrlHelpers.deleteProtocol(link),
            [SiteLinkField.protocol]:
                (UrlHelpers.extractProtocol(link) as ProtocolValue) ||
                items[action.index].protocol ||
                ProtocolValue.https
        };
    } else if (action.field === SiteLinkField.turbo) {
        items[action.index] = {
            ...items[action.index],
            [SiteLinkField.turbo]: undefined,
            isMassActionsTurboDifferent: false
        };
    } else {
        items[action.index] = {
            ...items[action.index],
            [action.field]: action.value
        };
    }

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items,
                errors: {
                    ...siteLinks.errors,
                    ...sitelinksErrors
                }
            }
        }
    };
};

const updateProtocolSiteLinks = (
    state: BannersSiteLinksEditorState,
    action: UpdateProtocolDraftSiteLinks
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const items = [...siteLinks.items];
    const sitelinksErrors = siteLinks.errors;

    if (sitelinksErrors[action.index]) {
        delete sitelinksErrors[action.index][SiteLinkField.link];
        if (isEmpty(sitelinksErrors[action.index])) {
            delete sitelinksErrors[action.index];
        }
    }

    items[action.index] = {
        ...items[action.index],
        protocol: action.value
    };

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items,
                errors: {
                    ...siteLinks.errors,
                    ...sitelinksErrors
                }
            }
        }
    };
};

const checkBannerSitelinkHrefStatus = (
    state: BannersSiteLinksEditorState,
    action: CheckBannerSitelinkHrefStatus
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const items = [...siteLinks.items];

    items[action.index] = {
        ...items[action.index],
        isUrlChecking: true
    };

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items
            }
        }
    };
};

const updateSiteLinkHrefData = (
    state: BannersSiteLinksEditorState,
    action: UpdateSiteLinkHrefStatus
): BannersSiteLinksEditorState => {
    const currentSiteLinkErrorData = state.data[action.bannerId].errors || {};
    const additionalSiteLinksErrorsKeys = Object.keys(currentSiteLinkErrorData).filter(
        (key: string) => (action.isValid && key !== String(action.index)) || !action.isValid
    );

    const siteLinks = state.data[action.bannerId];
    const items = [...siteLinks.items];
    items[action.index] = {
        ...items[action.index],
        isUrlChecking: false,
        isUrlValid: action.isValid
    };

    const siteLinkErrorData = additionalSiteLinksErrorsKeys.reduce((res, item) => {
        res[item] = currentSiteLinkErrorData[item];

        return res;
    }, {});

    const newError = action.isValid
        ? {}
        : {
              [action.index]: {
                  link: {
                      errors: [
                          {
                              text: action.text
                          }
                      ]
                  }
              }
          };

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items: items,
                errors: {
                    ...siteLinks.errors,
                    ...(additionalSiteLinksErrorsKeys.length || !action.isValid
                        ? {
                              ...siteLinkErrorData,
                              ...newError
                          }
                        : {})
                }
            }
        }
    };
};

const clearSitelinks = (
    state: BannersSiteLinksEditorState,
    action: ClearSiteLinks,
    fullState: State
): BannersSiteLinksEditorState => {
    const newSiteLinksData = action.bannerId.reduce((acc, item) => {
        acc[item] = {
            ...state.data[item],
            isSaving: false
        };

        return acc;
    }, {});

    return {
        ...state,
        data: {
            ...state.data,
            ...newSiteLinksData
        }
    };
};

const updateTurbolandingForSitelink = (
    state: BannersSiteLinksEditorState,
    action: UpdateTurbolandingForSitelink
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const items = [...siteLinks.items];

    items[action.index] = {
        ...items[action.index],
        turbo: action.landing,
        isTurboLoading: false
    };

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items
            }
        }
    };
};

const getTurbolandingForSitelink = (
    state: BannersSiteLinksEditorState,
    action: GetTurbolandingInfoForSitelink
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const items = [...siteLinks.items];
    const index = siteLinks.currentSitelink;

    items[index] = {
        ...items[index],
        isTurboLoading: true
    };

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                isCanvasOpened: false,
                items
            }
        }
    };
};

const saveSiteLinks = (
    state: BannersSiteLinksEditorState,
    action: SaveSiteLinksSuccess,
    fullState: State
): BannersSiteLinksEditorState => {
    const newSiteLinksData = action.bannerId.reduce((acc, item, index) => {
        acc[item] = {
            ...state.data[item],
            items: [...action.collapsedSiteLinks[index]],
            isSaving: false
        };

        return acc;
    }, {});

    return {
        ...state,
        data: {
            ...state.data,
            ...newSiteLinksData
        }
    };
};

const createDefaultSiteLinkData = (): SiteLinkData => {
    return {
        title: '',
        link: '',
        descr: '',
        isTurboLoading: false,
        isUrlChecking: false,
        isUrlValid: true,
        protocol: ProtocolValue.https
    };
};

const createDefaultSiteLinksEditor = (): BannerSiteLinksEditorData => {
    return {
        items: range(4).map(getDefaultSiteLinkData),
        errors: {},
        isDescriptionChecked: true,
        isSaving: false,
        isCanvasOpened: false,
        isUrlChecking: false,
        currentSitelink: 0,
        turboApiUrl: ''
    };
};

const createBannerSiteLinksDraftData = (
    state: BannersSiteLinksEditorState,
    action: CreateSiteLinksDraftDataAction
): BannersSiteLinksEditorState => {
    const banners = action.bannersData.banners;

    const newSiteLinks = Object.keys(banners).reduce(
        (acc, key) => {
            const currentBanner: EditedBanner = banners[key];

            if (bannerHasSitelinks(currentBanner)) {
                acc[key] = createSiteLinksByBannersApiData(currentBanner.sitelinks);
            }

            return acc;
        },
        {
            [MassActionId.bannersEdit]: createDefaultSiteLinksEditor()
        }
    );

    return {
        ...state,
        data: newSiteLinks
    };
};

const resetBannerSiteLinksFields = (
    state: BannersSiteLinksEditorState,
    action: ResetBannerSitelinkFieldsAction
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const items = siteLinks.items.map(
        (sitelink: SiteLinkData): SiteLinkData => {
            return createDefaultSiteLinkData();
        }
    );

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items,
                errors: {}
            }
        }
    };
};

export const addBannerSiteLinkReducer = (
    state: BannersSiteLinksEditorState,
    action: AddBannerSiteLink
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items: [...siteLinks.items, createDefaultSiteLinkData()]
            }
        }
    };
};

export const removeBannerSiteLinkReducer = (
    state: BannersSiteLinksEditorState,
    action: RemoveBannerSiteLink
): BannersSiteLinksEditorState => {
    const siteLinks = state.data[action.bannerId];
    const siteLinksErrors = siteLinks.errors;
    delete siteLinksErrors[action.index];

    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: {
                ...siteLinks,
                items: without(siteLinks.items, siteLinks.items[action.index]),
                errors: {
                    ...siteLinks.errors,
                    ...siteLinksErrors
                }
            }
        }
    };
};

export const setSitelinksErrors = (
    state: EditBannersState,
    bannerId: BannerId[],
    errors: object,
    fullState: State,
    collapsedSiteLinks?: Array<Array<SiteLinkData>>
): EditBannersState => {
    const newBanners = bannerId.reduce((acc, next, index) => {
        // ошибки могут приходить в поле sitelinks: {errors}, а могут ещё и лежать
        // в драфтовом состоянии.
        const currBanners = state.banners[next];
        const currSiteLinks = fullState.siteLinksEditor.data[next];
        const newSiteLinksErrors = get(errors[index], 'sitelinks', {}); // новые ошибки сайтлинков всегда лежат в sitelinks
        // дергаем текущие ошибки сайтлинков
        const bannerSiteLinksErrors = currSiteLinks.errors;

        // объединяем их
        let resultSiteLinksErrors = {
            ...newSiteLinksErrors,
            ...bannerSiteLinksErrors
        };

        // нужно отфильтровать общие ошибки на повторяющиеся
        if (resultSiteLinksErrors.errors) {
            resultSiteLinksErrors = {
                ...resultSiteLinksErrors,
                errors: uniqWith(resultSiteLinksErrors.errors, isEqual)
            };
        }

        if (!bannerHasSitelinks(currBanners)) {
            acc[next] = currBanners;
        } else {
            acc[next] = {
                ...currBanners,
                sitelinks: {
                    ...currBanners.sitelinks,
                    items: collapsedSiteLinks !== undefined ? [...collapsedSiteLinks[index]] : [...currSiteLinks.items],
                    isSaving: false
                },
                errors: {
                    ...currBanners.errors,
                    ...errors[index],
                    sitelinks: {
                        ...resultSiteLinksErrors
                    }
                }
            };
        }

        return acc;
    }, {});

    return {
        ...state,
        banners: {
            ...state.banners,
            ...newBanners
        },
        pageStatus: PageStatus.LOADED
    };
};

function createNewSiteLinksTextBanners(
    state: BannersSiteLinksEditorState,
    action: CreateNewTextBannerAction
): BannersSiteLinksEditorState {
    return {
        ...state,
        data: {
            [action.bannerId]: createDefaultSiteLinksEditor(),
            [MassActionId.bannersEdit]: createDefaultSiteLinksEditor()
        }
    };
}

const setSavedSiteLinks = (
    state: BannersSiteLinksEditorState,
    action: SaveBannersSuccess
): BannersSiteLinksEditorState => {
    const { added } = action.result;
    const siteLinksData = state.data;

    if (added.banners.length) {
        added.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = added.ids[index];
            const newId = String(addedBanner.id);

            siteLinksData[newId] = {
                ...siteLinksData[oldId]
            };

            delete siteLinksData[oldId];
        });

        state = {
            ...state,
            data: siteLinksData
        };
    }

    return state;
};

function addNewTextBanners(
    state: BannersSiteLinksEditorState,
    action: AddNewTextBannersAction
): BannersSiteLinksEditorState {
    const result: Record<BannerSiteLinksId, BannerSiteLinksEditorData> = action.banners.reduce(
        (data: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, banner) => {
            data[banner.bannerData.id] = createDefaultSiteLinksEditor();

            return data;
        },
        {}
    );

    return {
        ...state,
        data: {
            ...state.data,
            ...result
        }
    };
}

function copyBannerSitelinksInfoReducer(
    state: BannersSiteLinksEditorState,
    action: CopyBanner | CopyPrevBanner | OnCopyPrevEditedBanners
): BannersSiteLinksEditorState {
    const siteLinksInfo = { ...state.data[action.bannerId] };

    if (!siteLinksInfo) {
        return state;
    }

    return {
        ...state,
        data: {
            ...state.data,
            [action.newBannerId]: siteLinksInfo
        }
    };
}

function prepareMassActionsSiteLinksReducer(state: BannersSiteLinksEditorState): BannersSiteLinksEditorState {
    const allSiteLinksItems = Object.keys(state.data)
        .filter(bannerId => bannerId !== MassActionId.bannersEdit)
        .map(bannerId => state.data[bannerId].items);

    // в массовых нужно отобразить максимальный размер сайтлинков
    // пример - 3 баннера с 1, 3 и 7 сайтлинками - отобразим 7, для этого maxSize
    const maxSize = max(allSiteLinksItems.map(siteLinkSet => siteLinkSet.length)) || 0;

    const itemsForMassActions = range(maxSize).reduce((acc, index): Array<SiteLinkData> => {
        const currentSiteLinkSlice = allSiteLinksItems.map(siteLinksData => siteLinksData[index]);

        const uniqTitles = uniq(currentSiteLinkSlice.map(siteLinkData => siteLinkData && siteLinkData.title));
        let isMassActionsTitleDifferent;
        let title = '';
        if (uniqTitles.length > 1) {
            isMassActionsTitleDifferent = true;
        } else {
            title = uniqTitles[0] || '';
        }

        const uniqDescr = uniq(currentSiteLinkSlice.map(siteLinkData => siteLinkData && siteLinkData.descr));
        let isMassActionsDescrDifferent;
        let descr = '';
        if (uniqDescr.length > 1) {
            isMassActionsDescrDifferent = true;
        } else {
            descr = uniqDescr[0] || '';
        }

        const uniqLinks = uniq(currentSiteLinkSlice.map(siteLinkData => siteLinkData && siteLinkData.link));
        let isMassActionsLinkDifferent;
        let link = '';
        if (uniqLinks.length > 1) {
            isMassActionsLinkDifferent = true;
        } else {
            link = uniqLinks[0] || '';
        }

        const uniqProtocols = uniq(
            currentSiteLinkSlice.map(siteLinkData => (siteLinkData && siteLinkData.protocol) || ProtocolValue.https)
        );
        let isMassActionsProtocolDifferent;
        let protocol = ProtocolValue.https;
        if (uniqProtocols.length > 1) {
            isMassActionsProtocolDifferent = true;
            protocol = ProtocolValue.different;
        } else {
            protocol = uniqProtocols[0] || ProtocolValue.https;
        }

        const uniqTurbo = uniqBy(
            currentSiteLinkSlice.map(siteLinkData => siteLinkData && siteLinkData.turbo && siteLinkData.turbo),
            'landingId'
        );
        let turbo;
        let isMassActionsTurboDifferent;
        if (uniqTurbo.length > 1) {
            isMassActionsTurboDifferent = true;
        } else {
            turbo = uniqTurbo[0];
        }

        return [
            ...acc,
            {
                ...getDefaultSiteLinkData(),
                title,
                isMassActionsTitleDifferent,
                descr,
                isMassActionsDescrDifferent,
                link,
                protocol,
                isMassActionsLinkDifferent,
                turbo,
                isMassActionsTurboDifferent,
                isMassActionsProtocolDifferent,
                isPreparedSiteLinkFromMassAction: true
            }
        ];
    }, []);

    return {
        ...state,
        data: {
            ...state.data,
            [MassActionId.bannersEdit]: {
                ...state.data[MassActionId.bannersEdit],
                items: itemsForMassActions,
                errors: {}
            }
        }
    };
}

function setDefaultBannerSitelinksReducer(
    state: BannersSiteLinksEditorState,
    action: SetDefaultBannerSitelinks
): BannersSiteLinksEditorState {
    return {
        ...state,
        data: {
            ...state.data,
            [action.bannerId]: createDefaultSiteLinksEditor()
        }
    };
}

export const siteLinksInitialData = {
    data: {}
};

export function bannerSiteLinksEditorReducer(
    state: BannersSiteLinksEditorState = siteLinksInitialData,
    action: Action,
    fullState: State
): BannersSiteLinksEditorState {
    switch (action.type) {
        case RESET_BANNERS_EDIT:
            return siteLinksInitialData;
        case OPEN_SITE_LINKS_EDITOR:
            return openSiteLinksEditor(state, action, fullState);
        case OPEN_SITE_LINKS_CANVAS:
            return openSiteLinksCanvas(state, action, fullState);
        case CLOSE_SITE_LINKS_CANVAS:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        isCanvasOpened: false
                    }
                }
            };
        case GET_TURBOLANDING_INFO_FOR_SITELINK:
            return getTurbolandingForSitelink(state, action);
        case UPDATE_TURBOLANDING_INFO_FOR_SITELINK:
            return updateTurbolandingForSitelink(state, action);
        case UPDATE_DRAFT_SITE_LINKS:
            return updateSiteLinks(state, action);
        case CHECK_BANNER_SITELINK_HREF_STATUS:
            return checkBannerSitelinkHrefStatus(state, action);
        case UPDATE_SITE_LINK_HREF_STATUS:
            return updateSiteLinkHrefData(state, action);
        case SAVE_SITE_LINKS: {
            const newSiteLinks = action.bannerId.reduce(
                (acc: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, next: BannerId) => {
                    acc[next] = {
                        ...state.data[next],
                        isSaving: true
                    };

                    return acc;
                },
                {}
            );

            return {
                ...state,
                data: {
                    ...state.data,
                    ...newSiteLinks
                }
            };
        }
        case CLEAR_SITE_LINKS:
            return clearSitelinks(state, action, fullState);
        case SAVE_SITE_LINKS_SUCCESS:
            return saveSiteLinks(state, action, fullState);
        case TOGGLE_SITE_LINKS_DESCRIPTION_FLAG:
            return {
                ...state,
                data: {
                    ...state.data,
                    [action.bannerId]: {
                        ...state.data[action.bannerId],
                        isDescriptionChecked: !state.data[action.bannerId].isDescriptionChecked
                    }
                }
            };
        case RESET_BANNER_SITELINK_FIELDS:
            return resetBannerSiteLinksFields(state, action);
        case UPDATE_PROTOCOL_DRAFT_SITE_LINKS:
            return updateProtocolSiteLinks(state, action);
        case ADD_BANNER_SITELINK:
            return addBannerSiteLinkReducer(state, action);
        case REMOVE_BANNER_SITELINK:
            return removeBannerSiteLinkReducer(state, action);
        case SHOW_SITE_LINKS_ERRORS: {
            const newSiteLinks = action.bannerId.reduce(
                (acc: Record<BannerSiteLinksId, BannerSiteLinksEditorData>, next: BannerId, index: number) => {
                    const errors = action.errors.map(error =>
                        buildErrorTree(mapTextToErrors(error, sitelinksErrorsTextMapper))
                    );

                    const newErrors = get(errors[index], 'sitelinks', {});

                    if (newErrors.errors) {
                        delete newErrors.errors;
                    }

                    acc[next] = {
                        ...state.data[next],
                        items: action.collapsedSiteLinks[index],
                        errors: newErrors,
                        isSaving: false
                    };

                    return acc;
                },
                {}
            );

            return {
                ...state,
                data: {
                    ...state.data,
                    ...newSiteLinks
                }
            };
        }
        case ON_OPEN_MASS_ACTIONS_SITE_LINKS_EDITOR:
            return prepareMassActionsSiteLinksReducer(state);
        case ACCEPT_MASS_ACTIONS_SITE_LINKS_EDITOR:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...selectSiteLinksEditDataBySiteLinksReplace(fullState)
                }
            };
        case SAVE_MASS_ACTIONS_LINK_BULK_REPLACEMENT_EDITOR:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...selectBannersDraftEditSiteLinksDataByLinkReplace(fullState)
                }
            };
        case SAVE_MASS_ACTIONS_TEXT_BULK_REPLACEMENT_EDITOR:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...selectBannersDraftEditDataByTextReplace(fullState)
                }
            };
        case SAVE_MASS_ACTIONS_LINK_BULK_PATH_REPLACEMENT_EDITOR:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...selectBannersDraftEditSiteLinksDataByPartLinkReplace(fullState)
                }
            };
        case ON_ACCEPT_MASS_ACTIONS_TRACKING_PARAMETERS_BULK_REPLACEMENT_EDITOR:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...selectBannersDraftEditSiteLinksDataByTrackingParametersReplace(fullState)
                }
            };
        case CREATE_SITE_LINKS_DRAFT_DATA:
            return createBannerSiteLinksDraftData(state, action);
        case ADD_NEW_TEXT_BANNERS:
            return addNewTextBanners(state, action);
        case ON_COPY_PREV_EDITED_BANNER:
        case COPY_BANNER:
        case COPY_PREV_BANNER:
            return copyBannerSitelinksInfoReducer(state, action);
        case CREATE_NEW_TEXT_BANNER:
            return createNewSiteLinksTextBanners(state, action);
        case SAVE_BANNERS_SUCCESS:
            return setSavedSiteLinks(state, action);
        case SET_DEFAULT_BANNER_SITELINKS:
            return setDefaultBannerSitelinksReducer(state, action);
        case UPDATE_SITELINKS_DATA:
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.newSiteLinks
                }
            };
        default:
            return state;
    }
}
